/**
 * A small `?` beside something, carrying the explanation that would otherwise
 * be a paragraph.
 *
 * BEST's screens have a lot to explain, and the explanations are not decoration:
 * a total that is a floor, roots that overlap and must never be summed, a margin
 * that does not exist. Those facts change what a number MEANS, so they cannot be
 * dropped. But printed in full, above the thing they describe, they push the
 * actual answer below the fold and get skipped by the third visit - at which
 * point they are costing space and teaching nobody.
 *
 * So the rule this component exists to make easy: **the screen states what is
 * true, and the tip explains why.** A caption that a reader needs EVERY time
 * stays in the body; a caption that is self-evident once stays here.
 *
 * It is a BUTTON rather than a bare icon, deliberately. A tooltip attached to
 * something unfocusable is invisible to a keyboard and to a screen reader, which
 * turns "the explanation is one hover away" into "the explanation is gone" for
 * the people most likely to want it. `describeChild` makes the text the
 * accessible description rather than replacing the label, and the touch delays
 * make it work on a tablet, where there is no hover at all.
 */

// 'HelpOutline' is not a module in this version of @mui/icons-material -
// only its themed variants are. The Outlined one is the plain ringed '?'.
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import type { ReactNode } from 'react'

export default function HelpTip({
  title,
  label = 'Explain this',
  /** Match the surrounding type: `inherit` sits in a heading, `small` stands alone. */
  size = 'small',
}: {
  title: ReactNode
  /** What a screen reader announces. Say what it explains, not "help". */
  label?: string
  size?: 'inherit' | 'small' | 'medium'
}) {
  return (
    <Tooltip
      title={title}
      describeChild
      // A tap opens it at once and it stays long enough to read a sentence or
      // three; the defaults are tuned for one-word tooltips.
      enterTouchDelay={0}
      leaveTouchDelay={8000}
      slotProps={{ tooltip: { sx: { maxWidth: '46ch', fontSize: '0.8rem', lineHeight: 1.5 } } }}
    >
      <IconButton
        size="small"
        aria-label={label}
        // Quiet by default and only as loud as the text it sits beside. It is an
        // offer, not a warning: a `?` in the accent colour reads as something
        // unresolved on a screen whose whole job is reporting what is unresolved.
        sx={{
          color: 'text.disabled',
          p: 0.25,
          ml: 0.5,
          verticalAlign: 'middle',
          '&:hover': { color: 'text.secondary' },
        }}
      >
        <HelpOutlineIcon fontSize={size} />
      </IconButton>
    </Tooltip>
  )
}
